/**
 * Measure tool overlay — draws the ruler, guides and alignment outlines on top of the lobby and
 * handles all mouse / touch / key input. Same ground rules as figma/figmaOverlay.ts: the root is
 * click-through (only the handles take pointer events), and drags move the DOM directly and save
 * once on release. Styles: styles/measureOverlay.module.css; positions are CSS variables
 * (--pos, --length, --hit, --grip). Coordinates are CSS px of the lobby viewport.
 */

import { isCoarsePointer } from '../bridge/container';
import {
    ALIGNED_TOLERANCE,
    MEASURE_ROOT_ID,
    boxOf,
    boxesNear,
    collectElementBoxes,
    findAlignedBoxes,
    isOwnUi,
    lobbyElementAt,
    nearestSnapLine,
    sameBox,
    snapLinesOn,
} from './elementBoxes';
import type { Box, SnapLine } from './elementBoxes';
import { describeElement, infoCardClass, selectorOf } from './elementInfo';
import { MIN_RULER_LENGTH } from './measureController';
import type { MeasureController, MeasureGuide, MeasureRuler, MeasureState } from './measureController';
import styles from './styles/measureOverlay.module.css';
import { clamp, fontSizeOf, formatDistance, formatNumber, rulerTicks, viewportSizeOn } from './units';
import type { MeasureAxis } from './units';

const RULER_THICKNESS = 28;
const SNAP_DISTANCE = 4;
/** The ruler snaps to elements within this many px of it on the other axis. */
const RULER_SNAP_REACH = 32;
/** Kept free at the ruler's far end for the unit name. */
const UNIT_LABEL_SPACE = 30;
const MAX_OUTLINES = 80;
/** A second wrap within this many px of the first one wraps the parent. */
const SAME_SPOT_DISTANCE = 4;
const SCROLL_SETTLE_MS = 150;
const RULER_BACKGROUND = 'rgba(245, 229, 167, 0.45)';
const RULER_INK = '#111827';
const SYSTEM_FONT = '-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif';

type MeasureActions = Pick<
    MeasureController,
    'set' | 'setRuler' | 'setGuide' | 'removeGuide' | 'addGuide' | 'wrapBox' | 'reclampForViewport'
>;
type OverlayParts = ReturnType<typeof createOverlay>;
type Selection = { kind: 'guide'; id: string } | { kind: 'ruler' } | null;
/** `target` is the element snapped to (null when nothing, or another guide). */
type SnapResult = { pos: number; snapped: boolean; target: Box | null };

let parts: OverlayParts | null = null;
let state: MeasureState | null = null;
let actions: MeasureActions | null = null;
/** While a drag runs, re-renders are skipped — they'd replace the node under the pointer. */
let isDragging = false;
let pointer = { x: 0, y: 0 };
let readoutFrameId = 0;
/** Aborting it removes every document / window listener the overlay added. */
let listeners: AbortController | null = null;
let selection: Selection = null;
/** Element boxes from the last measurement; reused while a guide is dragged. */
let elementBoxes: Box[] = [];
let scrollSettleTimer: ReturnType<typeof setTimeout> | null = null;
/** "Wrap element…" mode: the next click wraps (touch has no Alt key). */
let isPicking = false;
let lastWrap: { element: Element; x: number; y: number } | null = null;
/** Element the info card currently describes. */
let infoTarget: Element | null = null;

/* ── Helpers ─────────────────────────────────────────────────────── */

function fromHtml(markup: string): HTMLElement {
    const template = document.createElement('template');
    template.innerHTML = markup.trim();
    return template.content.firstElementChild as HTMLElement;
}

/** The descendant marked `data-part="<name>"`. Missing markup is a bug, so it throws. */
function partOf<T extends HTMLElement = HTMLElement>(container: Element, name: string): T {
    const element = container.querySelector<T>(`[data-part="${name}"]`);
    if (!element) throw new Error(`Measure overlay: missing part "${name}"`);
    return element;
}

function boxStyle(box: Box): string {
    return `left:${box.left}px;top:${box.top}px;width:${box.right - box.left}px;height:${box.bottom - box.top}px`;
}

function isTyping(target: EventTarget | null): boolean {
    return target instanceof HTMLElement && (target.isContentEditable || !!target.closest('input, textarea, select'));
}

function setBodyDragStyle(cursor: string | null): void {
    document.body.style.cursor = cursor ?? '';
    document.body.style.userSelect = cursor ? 'none' : '';
}

/** Common start of every handle press: primary button only, select it, measure the page. */
function beginPress(pressEvent: PointerEvent, pressed: Selection): MeasureState | null {
    if (!state || pressEvent.button > 0) return null;
    select(pressed);
    elementBoxes = collectElementBoxes();
    return state;
}

/** Pointer-captured drag: `onMove` gets the distance moved so far, `onEnd` runs once on release. */
function startDrag(
    handle: HTMLElement,
    pressEvent: PointerEvent,
    cursor: string,
    onMove: (deltaX: number, deltaY: number, event: PointerEvent) => void,
    onEnd: () => void,
): void {
    pressEvent.preventDefault();
    pressEvent.stopPropagation();
    isDragging = true;
    try {
        handle.setPointerCapture(pressEvent.pointerId);
    } catch {
        // The pointer is already gone; the drag still works while it stays over the handle.
    }
    setBodyDragStyle(cursor);
    const drag = new AbortController();
    const { signal } = drag;
    const end = (event: PointerEvent): void => {
        if (event.pointerId !== pressEvent.pointerId) return;
        drag.abort();
        setBodyDragStyle(null);
        isDragging = false;
        showSnapTargets([]);
        onEnd();
    };
    handle.addEventListener('pointermove', (event) => {
        if (event.pointerId !== pressEvent.pointerId) return;
        onMove(event.clientX - pressEvent.clientX, event.clientY - pressEvent.clientY, event);
    }, { signal });
    handle.addEventListener('pointerup', end, { signal });
    handle.addEventListener('pointercancel', end, { signal });
}

/**
 * Snap lines on `axis` from the elements measured at press time, or null when snapping is off.
 * With `near` (the ruler's extent on the other axis) only elements beside it count and centers are
 * left out — for the ruler they're mostly noise. Guides use every element, centers included.
 */
function snapLinesFor(
    axis: MeasureAxis,
    { near, exceptGuideId }: { near?: [from: number, to: number]; exceptGuideId?: string },
): SnapLine[] | null {
    if (!state?.snap) return null;
    const boxes = near
        ? boxesNear(elementBoxes, axis, near[0] - RULER_SNAP_REACH, near[1] + RULER_SNAP_REACH)
        : elementBoxes;
    const guidePositions = state.guides
        .filter((guide) => guide.axis === axis && guide.id !== exceptGuideId)
        .map((guide) => guide.pos);
    return snapLinesOn(axis, boxes, guidePositions, !near);
}

/**
 * Snaps whichever edge lands closest to a snap line (Shift = no snap). `origin` is the element's
 * start, `edgeOffsets` its edges relative to it ([0, width] for both sides of the ruler).
 */
function snap(lines: SnapLine[] | null, event: PointerEvent, origin: number, edgeOffsets = [0]): SnapResult {
    let best: SnapResult = { pos: origin, snapped: false, target: null };
    if (!lines || event.shiftKey) return best;
    let bestDistance = Infinity;
    for (const offset of edgeOffsets) {
        const line = nearestSnapLine(lines, origin + offset, SNAP_DISTANCE);
        if (line && Math.abs(line.pos - (origin + offset)) < bestDistance) {
            best = { pos: line.pos - offset, snapped: true, target: line.box };
            bestDistance = Math.abs(line.pos - (origin + offset));
        }
    }
    return best;
}

/** Dashed outline on the element(s) a drag is snapped to, so it's clear what it caught. */
function showSnapTargets(targets: Array<Box | null>): void {
    if (!parts) return;
    parts.snapTargetsLayer.innerHTML = targets
        .filter((box): box is Box => box !== null)
        .map((box) => `<div class="${styles.snapTarget}" style="${boxStyle(box)}"></div>`)
        .join('');
}

function select(next: Selection): void {
    selection = next;
    if (!parts) return;
    parts.ruler.classList.toggle(styles.selected, selection?.kind === 'ruler');
    parts.guidesLayer.querySelectorAll<HTMLElement>('[data-guide-id]').forEach((guideElement) => {
        const isSelected = selection?.kind === 'guide' && selection.id === guideElement.dataset['guideId'];
        guideElement.classList.toggle(styles.selected, isSelected);
    });
}

/* ── Ruler ───────────────────────────────────────────────────────── */

function rulerAxis(ruler: MeasureRuler): MeasureAxis {
    return ruler.orientation === 'h' ? 'x' : 'y';
}

function rulerSize(ruler: MeasureRuler): { width: number; height: number } {
    return ruler.orientation === 'h'
        ? { width: ruler.length, height: RULER_THICKNESS }
        : { width: RULER_THICKNESS, height: ruler.length };
}

function placeRuler(rulerElement: HTMLElement, ruler: MeasureRuler): void {
    const { width, height } = rulerSize(ruler);
    rulerElement.classList.toggle(styles.horizontal, ruler.orientation === 'h');
    rulerElement.classList.toggle(styles.vertical, ruler.orientation === 'v');
    Object.assign(rulerElement.style, { left: `${ruler.x}px`, top: `${ruler.y}px`, width: `${width}px`, height: `${height}px` });
    rulerElement.style.setProperty('--length', `${ruler.length}px`);
}

function drawRuler(canvas: HTMLCanvasElement, ruler: MeasureRuler): void {
    const context = canvas.getContext('2d');
    if (!state || !context) return;
    const isHorizontal = ruler.orientation === 'h';
    const { width, height } = rulerSize(ruler);
    const pixelRatio = window.devicePixelRatio || 1;
    canvas.width = Math.round(width * pixelRatio);
    canvas.height = Math.round(height * pixelRatio);
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;

    context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    context.fillStyle = RULER_BACKGROUND;
    context.fillRect(0, 0, width, height);
    context.strokeStyle = RULER_INK;
    context.fillStyle = RULER_INK;
    context.lineWidth = 1;
    context.font = `500 10px ${SYSTEM_FONT}`;

    const ticks = rulerTicks(state, rulerAxis(ruler));
    const tickCount = Math.floor(ruler.length / ticks.stepPixels);
    context.beginPath();
    for (let index = 0; index <= tickCount; index++) {
        // +0.5 puts a 1px line on whole pixels instead of blurring across two.
        const position = Math.round(index * ticks.stepPixels) + 0.5;
        const tickLength = index % ticks.labelEvery === 0 ? 12 : index % ticks.majorEvery === 0 ? 8 : 5;
        context.moveTo(isHorizontal ? position : 0, isHorizontal ? 0 : position);
        context.lineTo(isHorizontal ? position : tickLength, isHorizontal ? tickLength : position);
    }
    context.stroke();

    for (let index = 0; index <= tickCount; index += ticks.labelEvery) {
        const position = Math.round(index * ticks.stepPixels);
        if (position > ruler.length - UNIT_LABEL_SPACE) break;
        context.save();
        if (isHorizontal) {
            context.textBaseline = 'top';
            context.translate(position + 2, 14);
        } else {
            // Numbers run along a vertical ruler, Photoshop-style.
            context.textBaseline = 'bottom';
            context.translate(14, position + 2);
            context.rotate(Math.PI / 2);
        }
        context.fillText(formatNumber(index * ticks.stepValue), 0, 0);
        context.restore();
    }

    // Unit name at the far end, so the scale is never ambiguous.
    context.font = `700 10px ${SYSTEM_FONT}`;
    context.textBaseline = 'bottom';
    context.textAlign = isHorizontal ? 'right' : 'left';
    context.fillText(state.unit, isHorizontal ? width - 3 : 3, isHorizontal ? height - 2 : height - 3);
}

function wireRuler({ ruler: rulerElement, canvas, toolbar, grip }: OverlayParts): void {
    toolbar.addEventListener('pointerdown', (event) => event.stopPropagation());
    toolbar.addEventListener('click', (event) => {
        const action = (event.target as HTMLElement).closest('button')?.dataset['action'];
        if (action === 'hide') actions?.set({ rulerVisible: false });
        if (action === 'rotate' && state) actions?.setRuler({ orientation: state.ruler.orientation === 'h' ? 'v' : 'h' });
    });

    rulerElement.addEventListener('pointerdown', (pressEvent) => {
        const current = beginPress(pressEvent, { kind: 'ruler' });
        if (!current) return;
        const start = { ...current.ruler };
        const { width, height } = rulerSize(start);
        let position = { x: start.x, y: start.y };
        startDrag(rulerElement, pressEvent, 'grabbing', (deltaX, deltaY, event) => {
            const left = start.x + deltaX;
            const top = start.y + deltaY;
            // Only elements beside the ruler count, and any side of it can snap.
            const x = snap(snapLinesFor('x', { near: [top, top + height] }), event, left, [0, width]);
            const y = snap(snapLinesFor('y', { near: [left, left + width] }), event, top, [0, height]);
            position = { x: x.pos, y: y.pos };
            rulerElement.style.left = `${position.x}px`;
            rulerElement.style.top = `${position.y}px`;
            rulerElement.classList.toggle(styles.snapped, x.snapped || y.snapped);
            showSnapTargets([x.target, y.target]);
        }, () => {
            rulerElement.classList.remove(styles.snapped);
            actions?.setRuler(position);
        });
    });

    grip.addEventListener('pointerdown', (pressEvent) => {
        const current = beginPress(pressEvent, { kind: 'ruler' });
        if (!current) return;
        const start = { ...current.ruler };
        const axis = rulerAxis(start);
        const origin = axis === 'x' ? start.x : start.y;
        const crossStart = axis === 'x' ? start.y : start.x;
        const lines = snapLinesFor(axis, { near: [crossStart, crossStart + RULER_THICKNESS] });
        let length = start.length;
        startDrag(grip, pressEvent, axis === 'x' ? 'ew-resize' : 'ns-resize', (deltaX, deltaY, event) => {
            const end = snap(lines, event, origin + start.length + (axis === 'x' ? deltaX : deltaY));
            length = Math.max(MIN_RULER_LENGTH, end.pos - origin);
            placeRuler(rulerElement, { ...start, length });
            drawRuler(canvas, { ...start, length });
            rulerElement.classList.toggle(styles.snapped, end.snapped);
            showSnapTargets([end.target]);
        }, () => {
            rulerElement.classList.remove(styles.snapped);
            actions?.setRuler({ length });
        });
    });
}

/** Toolbar below / right of the ruler; flipped above / left when that side is off-screen. */
function placeToolbar(toolbar: HTMLElement, ruler: MeasureRuler): void {
    const gap = RULER_THICKNESS + 4;
    if (ruler.orientation === 'h') {
        const flip = ruler.y + gap + toolbar.offsetHeight > window.innerHeight;
        toolbar.style.left = '0px';
        toolbar.style.top = flip ? `${-4 - toolbar.offsetHeight}px` : `${gap}px`;
    } else {
        const flip = ruler.x + gap + toolbar.offsetWidth > window.innerWidth;
        toolbar.style.left = flip ? `${-4 - toolbar.offsetWidth}px` : `${gap}px`;
        toolbar.style.top = '0px';
    }
}

/** Red marker + "→ distance from 0" where the pointer projects onto the ruler. */
function showPointerOnRuler(): void {
    readoutFrameId = 0;
    if (!state || !parts) return;
    const { marker, readout } = parts;
    const { ruler } = state;
    const axis = rulerAxis(ruler);
    const distance = axis === 'x' ? pointer.x - ruler.x : pointer.y - ruler.y;
    const isOnRuler = distance >= 0 && distance <= ruler.length;
    marker.style.display = isOnRuler ? 'block' : 'none';
    marker.style.left = axis === 'x' ? `${distance}px` : '';
    marker.style.top = axis === 'y' ? `${distance}px` : '';
    readout.textContent = isOnRuler
        ? `→ ${formatDistance(distance, state, axis)}`
        : `len ${formatDistance(ruler.length, state, axis)}`;
}

/* ── Guides ──────────────────────────────────────────────────────── */

function placeGuide(guideElement: HTMLElement, guide: MeasureGuide, snapped: boolean): void {
    if (!state) return;
    guideElement.style.setProperty('--pos', `${guide.pos}px`);
    guideElement.classList.toggle(styles.snapped, snapped);
    const label = formatDistance(guide.pos, state, guide.axis);
    partOf(guideElement, 'text').textContent = snapped ? `${label} ⦿` : label;
}

function buildGuide(guide: MeasureGuide): HTMLElement {
    const isVertical = guide.axis === 'x';
    const classes = `${styles.guide} ${isVertical ? styles.vertical : styles.horizontal} ${guide.wrap ? styles.wrap : ''}`;
    const guideElement = fromHtml(`
        <div class="${classes}" data-guide-id="${guide.id}">
            <div class="${styles.line}"></div>
            <div class="${styles.hit}" data-part="hit"></div>
            <div class="${styles.label} ${styles.badge}" data-part="badge">
                <span data-part="text"></span>
                <button type="button" class="${styles.icon}" data-part="remove" title="Remove guide" aria-label="Remove guide">×</button>
            </div>
        </div>`);
    placeGuide(guideElement, guide, false);

    const removeButton = partOf(guideElement, 'remove');
    removeButton.addEventListener('pointerdown', (event) => event.stopPropagation());
    removeButton.addEventListener('click', () => actions?.removeGuide(guide.id));

    const onPress = (pressEvent: PointerEvent): void => {
        if (!beginPress(pressEvent, { kind: 'guide', id: guide.id })) return;
        const lines = snapLinesFor(guide.axis, { exceptGuideId: guide.id });
        const viewportSize = viewportSizeOn(guide.axis);
        let draggedTo = guide.pos;
        let finalPos = guide.pos;
        const cursor = isVertical ? 'ew-resize' : 'ns-resize';
        startDrag(pressEvent.currentTarget as HTMLElement, pressEvent, cursor, (deltaX, deltaY, event) => {
            draggedTo = guide.pos + (isVertical ? deltaX : deltaY);
            const snapped = snap(lines, event, draggedTo);
            showSnapTargets([snapped.target]);
            finalPos = snapped.pos;
            placeGuide(guideElement, { ...guide, pos: finalPos }, snapped.snapped);
            const liveGuides = (state?.guides ?? []).map((other) => (other.id === guide.id ? { ...other, pos: finalPos } : other));
            renderGuideDistances(liveGuides);
            renderAlignment(liveGuides);
        }, () => {
            // Dragged off-screen → delete (Figma / Photoshop habit).
            if (draggedTo < 0 || draggedTo > viewportSize) actions?.removeGuide(guide.id);
            // A click without movement only selects — it must not turn a wrap guide into a manual one.
            else if (finalPos !== guide.pos) actions?.setGuide(guide.id, finalPos);
        });
    };
    partOf(guideElement, 'hit').addEventListener('pointerdown', onPress);
    partOf(guideElement, 'badge').addEventListener('pointerdown', onPress);
    return guideElement;
}

/** Dashed line + distance label between each pair of neighbouring parallel guides. */
function renderGuideDistances(guides: MeasureGuide[]): void {
    if (!parts || !state) return;
    let markup = '';
    for (const axis of ['x', 'y'] as const) {
        const positions = guides.filter((guide) => guide.axis === axis).map((guide) => guide.pos).sort((a, b) => a - b);
        for (let index = 1; index < positions.length; index++) {
            const from = positions[index - 1];
            const distance = positions[index] - from;
            if (distance < 1) continue;
            const middle = from + distance / 2;
            const [lineStyle, labelStyle] =
                axis === 'x'
                    ? [`left:${from}px;width:${distance}px;top:40px;border-top-width:1px`, `left:${middle}px;top:32px;transform:translateX(-50%)`]
                    : [`top:${from}px;height:${distance}px;left:40px;border-left-width:1px`, `top:${middle}px;left:46px;transform:translateY(-50%)`];
            markup +=
                `<div class="${styles.distance}" style="${lineStyle}"></div>` +
                `<div class="${styles.label}" style="${labelStyle}">${formatDistance(distance, state, axis)}</div>`;
        }
    }
    parts.distancesLayer.innerHTML = markup;
}

function renderGuides(): void {
    if (!parts || !state) return;
    parts.guidesLayer.replaceChildren(...state.guides.map(buildGuide));
    renderGuideDistances(state.guides);
    select(selection);
}

/* ── Alignment outlines ──────────────────────────────────────────── */

function isAlignmentOn(): boolean {
    return !!state?.highlightAligned && state.guides.length > 0;
}

/** Green outline = an edge on a guide; red outline + offset tag = misses it by ≤ 3px. Uses `elementBoxes`. */
function renderAlignment(guides: MeasureGuide[]): void {
    if (!parts || !state) return;
    const layer = parts.alignmentLayer;
    layer.innerHTML = '';
    layer.style.display = 'block';
    if (!state.highlightAligned || guides.length === 0) return;

    const isNearMiss = (offset: number): boolean => Math.abs(offset) > ALIGNED_TOLERANCE;
    // Near-misses first — they're the bugs, so they win when the outline cap is hit.
    const matches = findAlignedBoxes(elementBoxes, guides).sort(
        (a, b) => Number(isNearMiss(b.offset)) - Number(isNearMiss(a.offset)),
    );
    const outlines = new Map<Box, boolean>(); // box → is it a near-miss
    let tagsHtml = '';
    let tagCount = 0;
    for (const { box, axis, edge, offset } of matches) {
        if (outlines.size >= MAX_OUTLINES) break;
        const nearMiss = isNearMiss(offset);
        if (!outlines.has(box)) outlines.set(box, nearMiss);
        if (!nearMiss || tagCount >= MAX_OUTLINES) continue;
        tagCount += 1;
        const position =
            axis === 'x'
                ? `left:${edge}px;top:${Math.max(0, box.top + 2)}px;transform:translateX(-50%)`
                : `top:${edge}px;left:${Math.max(0, box.left + 2)}px;transform:translateY(-50%)`;
        const text = (offset > 0 ? '+' : '−') + formatDistance(Math.abs(offset), state, axis);
        tagsHtml += `<div class="${styles.label} ${styles.nearMiss}" style="${position}">${text}</div>`;
    }
    const outlinesHtml = Array.from(outlines, ([box, nearMiss]) => {
        const classes = nearMiss ? `${styles.outline} ${styles.nearMiss}` : styles.outline;
        return `<div class="${classes}" style="${boxStyle(box)}"></div>`;
    }).join('');
    // Outlines first, so the offset tags paint on top of them.
    layer.innerHTML = outlinesHtml + tagsHtml;
}

/** Re-reads element positions and redraws the outlines (state change, scroll stop, resize). */
function remeasureAlignment(): void {
    if (!state) return;
    elementBoxes = isAlignmentOn() ? collectElementBoxes() : [];
    renderAlignment(state.guides);
}

/* ── Wrap element ────────────────────────────────────────────────── */

/** The element a wrap at (x, y) would frame. Again on the same spot → nearest ancestor with a different size. */
function wrapTargetAt(x: number, y: number): Element | null {
    const element = lobbyElementAt(x, y);
    if (!element || !lastWrap) return element;
    const isSameSpot =
        Math.abs(lastWrap.x - x) <= SAME_SPOT_DISTANCE &&
        Math.abs(lastWrap.y - y) <= SAME_SPOT_DISTANCE &&
        lastWrap.element.contains(element);
    if (!isSameSpot) return element;
    const wrappedBox = boxOf(lastWrap.element);
    for (let parent = lastWrap.element.parentElement; parent && parent !== document.body; parent = parent.parentElement) {
        if (!sameBox(boxOf(parent), wrappedBox)) return parent;
    }
    return lastWrap.element;
}

/** Below the element, else above it, else (tall element) beside it — never on top of what's measured. */
function placeInfoCard(info: HTMLElement, box: Box): void {
    const gap = 6;
    const cardWidth = info.offsetWidth;
    const cardHeight = info.offsetHeight;
    let left = box.left;
    let top = box.bottom + gap;
    if (top + cardHeight > window.innerHeight) {
        if (box.top - gap - cardHeight >= 0) {
            top = box.top - gap - cardHeight;
        } else {
            top = box.top;
            left = box.right + gap + cardWidth <= window.innerWidth ? box.right + gap : box.left - gap - cardWidth;
        }
    }
    info.style.left = `${clamp(left, 4, window.innerWidth - cardWidth - 4)}px`;
    info.style.top = `${clamp(top, 4, window.innerHeight - cardHeight - 4)}px`;
}

/** Dashed purple frame + info card (selector, size, styles) on the element a wrap would pick. */
function showWrapPreview(x: number, y: number): void {
    const target = wrapTargetAt(x, y);
    if (!parts || !state || !target) return hideWrapPreview();
    const box = boxOf(target);
    parts.preview.style.cssText = `display:block;${boxStyle(box)}`;
    // Computed styles are only re-read when the pointer reaches another element.
    if (target !== infoTarget) {
        infoTarget = target;
        parts.info.innerHTML = describeElement(target, state);
    }
    parts.info.style.display = 'block';
    placeInfoCard(parts.info, box);
}

function hideWrapPreview(): void {
    if (parts) {
        parts.preview.style.display = 'none';
        parts.info.style.display = 'none';
    }
    infoTarget = null;
}

function wrapElementAt(x: number, y: number): void {
    const target = wrapTargetAt(x, y);
    if (!target) return;
    lastWrap = { element: target, x, y };
    const box = boxOf(target);
    const reference = { label: selectorOf(target), width: box.right - box.left, height: box.bottom - box.top, fontSize: fontSizeOf(target) };
    actions?.wrapBox(box, reference);
}

function setPicking(on: boolean): void {
    isPicking = on;
    if (parts) parts.banner.style.display = on ? 'block' : 'none';
    if (on) showWrapPreview(pointer.x, pointer.y);
    else hideWrapPreview();
}

/* ── Input ───────────────────────────────────────────────────────── */

/** Arrow keys move the selected guide / ruler. Returns false when there was nothing to move. */
function nudgeSelection(key: string, step: number): boolean {
    if (!state || !selection) return false;
    const deltaX = key === 'ArrowLeft' ? -step : key === 'ArrowRight' ? step : 0;
    const deltaY = key === 'ArrowUp' ? -step : key === 'ArrowDown' ? step : 0;
    if (selection.kind === 'ruler') {
        actions?.setRuler({ x: state.ruler.x + deltaX, y: state.ruler.y + deltaY });
        return true;
    }
    const selectedId = selection.id;
    const guide = state.guides.find((candidate) => candidate.id === selectedId);
    const delta = guide?.axis === 'x' ? deltaX : deltaY;
    // ↑/↓ on a vertical guide (and ←/→ on a horizontal one) is left to the page — it scrolls.
    if (!guide || delta === 0) return false;
    actions?.setGuide(guide.id, guide.pos + delta);
    return true;
}

function onPointerMove(event: PointerEvent): void {
    pointer = { x: event.clientX, y: event.clientY };
    if (state?.rulerVisible && !readoutFrameId) readoutFrameId = requestAnimationFrame(showPointerOnRuler);
    if (isDragging) return;
    if (isPicking || event.altKey) showWrapPreview(event.clientX, event.clientY);
    else hideWrapPreview();
}

function onKeyDown(event: KeyboardEvent): void {
    if (isTyping(event.target)) return;
    if (event.key === 'Alt') return showWrapPreview(pointer.x, pointer.y);
    if (event.key === 'Escape') return isPicking ? setPicking(false) : select(null);
    if (event.ctrlKey || event.metaKey || event.altKey) return;
    if (event.key.startsWith('Arrow')) {
        if (nudgeSelection(event.key, event.shiftKey ? 10 : 1)) event.preventDefault();
    } else if ((event.key === 'Delete' || event.key === 'Backspace') && selection?.kind === 'guide') {
        event.preventDefault();
        actions?.removeGuide(selection.id);
        select(null);
    } else if (!event.repeat && event.key.toLowerCase() === 'h') {
        actions?.addGuide('y', pointer.y);
    } else if (!event.repeat && event.key.toLowerCase() === 'v') {
        actions?.addGuide('x', pointer.x);
    }
}

function onKeyUp(event: KeyboardEvent): void {
    if (event.key === 'Alt' && !isPicking) hideWrapPreview();
}

/**
 * A wrap click must not reach the lobby — on a game card it would launch the game. Document capture
 * runs before React's root listeners, so stopping here is enough; press / release are swallowed too,
 * so carousels don't start a drag. Returns whether it was a wrap click.
 */
function swallowWrapClick(event: MouseEvent): boolean {
    const isWrapClick = (isPicking || event.altKey) && event.target instanceof Element && !isOwnUi(event.target);
    if (!isWrapClick) return false;
    event.preventDefault();
    event.stopImmediatePropagation();
    return true;
}

function onClick(event: MouseEvent): void {
    if (!swallowWrapClick(event)) return;
    wrapElementAt(event.clientX, event.clientY);
    if (isPicking) setPicking(false);
}

function onPointerDown(event: PointerEvent): void {
    // A plain press on the lobby drops the selection, so arrow keys scroll again.
    if (!isPicking && !event.altKey && event.target instanceof Element && !isOwnUi(event.target)) select(null);
    swallowWrapClick(event);
}

function onScroll(): void {
    if (!isAlignmentOn()) return;
    // The outlines are glued to content that is moving — hide them until it stops.
    if (parts) parts.alignmentLayer.style.display = 'none';
    if (scrollSettleTimer) clearTimeout(scrollSettleTimer);
    scrollSettleTimer = setTimeout(() => {
        scrollSettleTimer = null;
        remeasureAlignment();
    }, SCROLL_SETTLE_MS);
}

function addListeners(): void {
    listeners = new AbortController();
    const { signal } = listeners;
    const capture = { capture: true, signal };
    const passiveCapture = { capture: true, passive: true, signal };
    document.addEventListener('pointermove', onPointerMove, passiveCapture);
    document.addEventListener('keydown', onKeyDown, { signal });
    document.addEventListener('keyup', onKeyUp, { signal });
    document.addEventListener('pointerdown', onPointerDown, capture);
    document.addEventListener('mousedown', swallowWrapClick, capture);
    document.addEventListener('pointerup', swallowWrapClick, capture);
    document.addEventListener('mouseup', swallowWrapClick, capture);
    document.addEventListener('click', onClick, capture);
    // Scroll doesn't bubble; capture on the document sees every scroll container.
    document.addEventListener('scroll', onScroll, passiveCapture);
    window.addEventListener('resize', () => actions?.reclampForViewport(), { signal });
}

/* ── Lifecycle ───────────────────────────────────────────────────── */

function createOverlay() {
    const root = fromHtml(`
        <div id="${MEASURE_ROOT_ID}" class="${styles.root}">
            <div class="${styles.layer}" data-part="alignment"></div>
            <div class="${styles.layer}" data-part="distances"></div>
            <div class="${styles.layer}" data-part="guides"></div>
            <div class="${styles.layer}" data-part="snapTargets"></div>
            <div class="${styles.ruler}" data-part="ruler">
                <canvas data-part="canvas"></canvas>
                <div class="${styles.marker}" data-part="marker"></div>
                <div class="${styles.label} ${styles.toolbar}" data-part="toolbar">
                    <span class="${styles.readout}" data-part="readout"></span>
                    <button type="button" class="${styles.icon}" data-action="rotate" title="Rotate ruler" aria-label="Rotate ruler">⟲</button>
                    <button type="button" class="${styles.icon}" data-action="hide" title="Hide ruler" aria-label="Hide ruler">×</button>
                </div>
                <div class="${styles.grip}" data-part="grip" title="Drag to change length"></div>
            </div>
            <div class="${styles.preview}" data-part="preview"></div>
            <div class="${infoCardClass}" data-part="info"></div>
            <div class="${styles.label} ${styles.banner}" data-part="banner">Click an element to wrap it · Esc cancels</div>
        </div>`);
    // Bigger grab areas for fingers.
    root.style.setProperty('--hit', isCoarsePointer() ? '20px' : '7px');
    root.style.setProperty('--grip', isCoarsePointer() ? '22px' : '12px');
    const part = (name: string): HTMLElement => partOf(root, name);
    return {
        root,
        alignmentLayer: part('alignment'),
        distancesLayer: part('distances'),
        guidesLayer: part('guides'),
        snapTargetsLayer: part('snapTargets'),
        ruler: part('ruler'),
        canvas: partOf<HTMLCanvasElement>(root, 'canvas'),
        marker: part('marker'),
        toolbar: part('toolbar'),
        readout: part('readout'),
        grip: part('grip'),
        preview: part('preview'),
        info: part('info'),
        banner: part('banner'),
    };
}

function render(newState: MeasureState): void {
    state = newState;
    // Drop a selection whose guide was removed or whose ruler was hidden.
    const selectedGuideId = selection?.kind === 'guide' ? selection.id : null;
    if (selectedGuideId && !newState.guides.some((guide) => guide.id === selectedGuideId)) selection = null;
    if (selection?.kind === 'ruler' && !newState.rulerVisible) selection = null;
    if (isDragging || !parts) return;

    parts.ruler.style.display = newState.rulerVisible ? 'block' : 'none';
    placeRuler(parts.ruler, newState.ruler);
    // Readout first — the toolbar's size decides which side of the ruler it goes.
    showPointerOnRuler();
    placeToolbar(parts.toolbar, newState.ruler);
    drawRuler(parts.canvas, newState.ruler);
    renderGuides();
    remeasureAlignment();
}

/** Shows the overlay, or re-renders it if it's already on screen. */
export function mount(newState: MeasureState, controller: MeasureActions): void {
    actions = controller;
    if (!parts) {
        pointer = { x: window.innerWidth / 2, y: window.innerHeight / 2 };
        parts = createOverlay();
        wireRuler(parts);
        document.body.appendChild(parts.root);
        addListeners();
    }
    render(newState);
}

/** "Wrap element…": the next click / tap on the lobby wraps the element under it. */
export function startPick(): void {
    if (!parts) return;
    lastWrap = null;
    setPicking(true);
}

export function destroy(): void {
    listeners?.abort();
    if (readoutFrameId) cancelAnimationFrame(readoutFrameId);
    if (scrollSettleTimer) clearTimeout(scrollSettleTimer);
    if (isDragging) setBodyDragStyle(null);
    parts?.root.remove();
    parts = listeners = scrollSettleTimer = state = actions = selection = lastWrap = infoTarget = null;
    readoutFrameId = 0;
    isDragging = isPicking = false;
    elementBoxes = [];
}
